import type { AuditLogItem, AuditAction, OperationEntityType, PaymentAuditLogItem, OrganizationAuditGroup } from "../types/auditoria-api.types";
import type { AuditoriaRow, AuditoriaFlujo, AuditoriaAccion } from "../types/auditoria.types";

export function mapEntityTypeToFlujo(
  entityType: OperationEntityType
): AuditoriaFlujo {
  switch (entityType) {
    case "PAYMENT":
      return "pago";
    case "PAYMENT_CONFIRMATION":
      return "confirmacion";
    case "NOTE":
      return "nota";
    default:
      return "confirmacion";
  }
}

export function mapActionToAccion(action: AuditAction): AuditoriaAccion {
  switch (action) {
    case "STATUS_CHANGE":
    case "TAKE":
    case "RELEASE":
      return "cambio_estado";
    case "CREATE":
      return "creacion";
    case "DELETE":
      return "eliminacion";
    case "UPDATE":
    case "RESTORE":
    case "VIEW":
      return "actualizacion_datos";
    default:
      return "actualizacion_datos";
  }
}

/**
 * Extracts a value from new_data or previous_data for display (e.g. amount, beneficiary).
 */
function getDataValue(
  data: Record<string, unknown> | null | undefined,
  key: string
): string | number | null {
  if (!data || typeof data !== "object") return null;
  const value = data[key];
  if (value === undefined || value === null) return null;
  return typeof value === "number" ? value : String(value);
}

/**
 * Formats amount for the table (item.amount, new_data.amount or previous_data.amount).
 */
function formatMonto(item: AuditLogItem): string {
  const amount =
    item.amount != null
      ? item.amount
      : getDataValue(item.new_data, "amount") ?? getDataValue(item.previous_data, "amount");
  if (amount === null) return "—";
  const num = typeof amount === "number" ? amount : Number(amount);
  if (Number.isNaN(num)) return "—";
  return new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency: "COP",
  }).format(num);
}

function formatAuditTableAmount(
  amount: number | null | undefined,
  currencyCode: string | null | undefined
): string {
  if (amount == null || Number.isNaN(Number(amount))) return "—";
  const num = Number(amount);
  const code = (currencyCode?.trim() || "COP").toUpperCase();
  try {
    return new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: code,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(num);
  } catch {
    return new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: "COP",
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(num);
  }
}

function getCodigoReferencia(item: AuditLogItem): string {
  if (item.reference_code != null && String(item.reference_code).trim()) return item.reference_code.trim();
  const fromNew = getDataValue(item.new_data, "reference_code");
  if (fromNew !== null && String(fromNew).trim()) return String(fromNew).trim();
  const fromPrev = getDataValue(item.previous_data, "reference_code");
  if (fromPrev !== null && String(fromPrev).trim()) return String(fromPrev).trim();
  return "—";
}

function getArchivosUrls(item: AuditLogItem): string[] {
  const urls: string[] = [];
  if (item.attachment_url != null) {
    if (Array.isArray(item.attachment_url)) urls.push(...item.attachment_url);
    else if (typeof item.attachment_url === "string") urls.push(item.attachment_url);
  }
  if (item.urls_anexos?.length) urls.push(...item.urls_anexos);
  if (item.attachment_urls?.length) urls.push(...item.attachment_urls);
  return urls;
}

function getComprobanteIndex(item: AuditLogItem): number {
  const hasComprobante =
    (item.attachment_url != null &&
      (Array.isArray(item.attachment_url) ? item.attachment_url.length > 0 : typeof item.attachment_url === "string"));
  return hasComprobante ? 0 : -1;
}

/**
 * Maps an API item (AuditLogItem) to a table row (AuditoriaRow).
 * Keeps UI business logic (flow, action, status, created by, taken by).
 */
export function mapAuditLogItemToRow(item: AuditLogItem): AuditoriaRow {
  const currencyRaw = item.currency?.trim() ?? "";
  return {
    id: item.id,
    entityId: item.entity_id,
    entityType: item.entity_type,
    fecha: item.created_at,
    codigoReferencia: getCodigoReferencia(item),
    flujo: mapEntityTypeToFlujo(item.entity_type),
    accion: mapActionToAccion(item.action),
    monto: formatMonto(item),
    estado: item.new_status ?? item.previous_status ?? "PENDING",
    creadoPor: item.user_email || item.user_name || "—",
    tomadoPor: item.taken_by_user?.email ?? item.taken_by_user?.full_name ?? "",
    archivosUrls: getArchivosUrls(item),
    comprobanteIndex: getComprobanteIndex(item),
    deletion_note: item.deletion_note ?? undefined,
    tableClientName: item.client_name?.trim() || "—",
    tablePayerName: item.payer_name?.trim() || "",
    tableCurrency: currencyRaw || "—",
    tableAmount: formatAuditTableAmount(item.amount, item.currency),
    tableOrganizationName: undefined,
    tableSubclientName:
      (item.payer_name?.trim() ?? "") !== (item.client_name?.trim() ?? "")
        ? (item.payer_name?.trim() ?? "")
        : "",
  };
}

export function mapAuditLogItemsToRows(items: AuditLogItem[]): AuditoriaRow[] {
  return items.map(mapAuditLogItemToRow);
}

export function mapPaymentAuditItemToRow(item: PaymentAuditLogItem): AuditoriaRow {
  return mapAuditLogItemToRow({
    id: item.id,
    entity_type: "PAYMENT" as OperationEntityType,
    entity_id: item.entity_id,
    action: item.action,
    previous_status: item.previous_status ?? null,
    new_status: item.new_status ?? null,
    previous_data: item.previous_data ?? null,
    new_data: item.new_data ?? null,
    duration_minutes: item.duration_minutes ?? null,
    description: item.description ?? null,
    user_id: item.user_id,
    user_email: item.user_email,
    user_name: item.user_name,
    ip_address: item.ip_address ?? null,
    user_agent: item.user_agent ?? null,
    created_at: item.created_at,
    user: item.user ? { id: item.user.id, full_name: item.user.full_name, email: item.user.email } : undefined,
    client_name: item.client_name ?? null,
    payer_name: item.payer_name ?? null,
    currency: item.currency ?? null,
    reference_code: item.reference_code ?? null,
    amount: item.amount ?? null,
    taken_by_user: item.taken_by_user,
    attachment_url: item.attachment_url ?? null,
    urls_anexos: null,
    attachment_urls: null,
  });
}

export function mapPaymentAuditItemsToRows(items: PaymentAuditLogItem[]): AuditoriaRow[] {
  return items.map(mapPaymentAuditItemToRow);
}

export function mapOrganizationGroupsToRows(groups: OrganizationAuditGroup[]): AuditoriaRow[] {
  return groups.flatMap((group) =>
    group.logs.map((log) => ({
      ...mapPaymentAuditItemToRow(log),
      tableOrganizationName: group.organization_name,
    }))
  );
}
